export type RandomSeed = number | string;

function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function normalizeSeed(seed: RandomSeed): number {
  if (typeof seed === "string") return hashString(seed);
  if (!Number.isFinite(seed)) {
    throw new RangeError("A random seed must be a finite number or a string.");
  }
  return Math.trunc(seed) >>> 0;
}

/** Small, reproducible PRNG based on Mulberry32. Never uses Math.random(). */
export class SeededRng {
  private state: number;

  public constructor(seed: RandomSeed) {
    this.state = normalizeSeed(seed);
  }

  public next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let value = this.state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  }

  public integer(min: number, max: number): number {
    if (!Number.isInteger(min) || !Number.isInteger(max) || max < min) {
      throw new RangeError(`Invalid integer range: ${min}..${max}`);
    }
    return min + Math.floor(this.next() * (max - min + 1));
  }

  public boolean(probability = 0.5): boolean {
    if (probability < 0 || probability > 1) {
      throw new RangeError("Probability must be between 0 and 1.");
    }
    return this.next() < probability;
  }

  public pick<T>(values: readonly T[]): T {
    if (values.length === 0) throw new RangeError("Cannot pick from an empty array.");
    return values[this.integer(0, values.length - 1)] as T;
  }

  public shuffle<T>(values: readonly T[]): T[] {
    const result = [...values];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapWith = this.integer(0, index);
      [result[index], result[swapWith]] = [result[swapWith] as T, result[index] as T];
    }
    return result;
  }

  public getState(): number {
    return this.state;
  }
}
